import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import userService, { CreateUserRequest } from '../../../services/userService';

const UserCreate: React.FC = () => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'ADMIN',
    password: '',
    passwordChangeRequired: true
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { isSystemAdmin } = useAuth();
  const navigate = useNavigate();

  // Redirect if not system admin
  useEffect(() => {
    if (!isSystemAdmin()) {
      navigate('/admin/dashboard');
    }
  }, [isSystemAdmin, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const newUser = await userService.createUser(formData);
      setSuccess('User created successfully');
      
      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'ADMIN',
        password: '',
        passwordChangeRequired: true
      });
      
      // Navigate to the user detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/users/${newUser.id}`);
      }, 1500);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError('Failed to create user. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/users', { replace: true });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Create New Admin User
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={formData.role}
                label="Role"
                onChange={(e) => {
                  if (e.target && 'name' in e.target && 'value' in e.target) {
                    handleInputChange(e as React.ChangeEvent<{ name?: string; value: unknown }>);
                  }
                }}
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="SYSTEM_ADMIN">System Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.passwordChangeRequired}
                  onChange={handleSwitchChange}
                  name="passwordChangeRequired"
                  color="primary"
                />
              }
              label="Require Password Change on First Login"
              sx={{ mt: 2 }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserCreate;
